import { useState, useEffect, useRef, useCallback } from "react";
import { PAGE_LOADING_SURFACES, TIPS } from "../onboarding/tips.js";
import {
  anchorSelector,
  countVisit,
  DWELL_MS,
  eligibleTips,
  NEXT_TIP_GAP_MS,
  readVisitState,
  SNOOZE_AFTER_MS,
  tipsReadiedByIntent,
  tipsRetiredByIntent,
  tipsRetiredBySurfaces,
  tipsRetiredByClick,
  tipsRetiredByControls,
  trackSkippablePrereqs,
  tryReadTipState,
  updateTipState,
  writeVisitState,
} from "../lib/onboarding/tipEngine.js";
import { fitsBeside } from "../lib/onboarding/tipPlacement.js";
import { onIntentBegin } from "../lib/telemetry/instrument";
import { recordUiEvent } from "../lib/logRingBuffer";
import { defaultSessionStorage } from "../lib/util/sessionStorage.js";

const POLL_MS = 250;
/** After a press elsewhere, how long a tip waits for that press to turn out to be its feature. */
const PRESS_END_MS = 1000;

const TIP_BY_ID = new Map(TIPS.map((t) => [t.id, t]));

const defaultLocalStorage = () => {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
};

const rectOf = (el) => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

const sameRect = (a, b) =>
  a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

/**
 * Whether `el` is really on screen: it has a box in the viewport and neither it nor an ancestor
 * is hidden. Closed drawers stay mounted at opacity 0, so being in the DOM is not enough.
 */
function isShown(el) {
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return false;
  if (r.bottom <= 0 || r.right <= 0 || r.top >= window.innerHeight || r.left >= window.innerWidth)
    return false;
  for (let e = el; e && e !== document.body; e = e.parentElement) {
    const cs = getComputedStyle(e);
    if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") return false;
  }
  return true;
}

/** Whether a surface is on screen (see isShown). */
const surfaceShown = (name) =>
  [...document.querySelectorAll(`[data-surface="${name}"]`)].some(isShown);

/** Whether a `data-ui` control is on screen (see isShown). */
const controlShown = (uiId) => [...document.querySelectorAll(`[data-ui="${uiId}"]`)].some(isShown);

/**
 * The first on-screen element for a tip's anchors: it has a box, overlaps the viewport, and is
 * not covered at its centre (a modal or drawer over it hides the tip).
 */
function findAnchor(tip) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  for (const spec of tip.anchor) {
    for (const el of document.querySelectorAll(anchorSelector(spec))) {
      // A disabled or inert control can't do what its tip says (a report not written yet).
      if (el.matches?.(":disabled") || el.closest?.('[aria-disabled="true"], [inert]')) continue;
      const r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) continue;
      if (r.bottom <= 0 || r.right <= 0 || r.top >= vh || r.left >= vw) continue;
      const x = Math.min(Math.max(r.left + r.width / 2, 0), vw - 1);
      const y = Math.min(Math.max(r.top + r.height / 2, 0), vh - 1);
      // The topmost element there other than the tip card itself, which may sit over the spot.
      const hit = document.elementsFromPoint(x, y).find((e) => !e.closest?.("[data-tip-layer]"));
      if (hit && (el === hit || el.contains(hit))) return el;
    }
  }
  return null;
}

/**
 * First-contact tips (onboarding/tips.js): shows at most one at a time beside its anchor and
 * retires each for good when its feature is used (an intent begins, a surface appears), when
 * dismissed, or — for `once` tips — when shown. Off entirely while `enabled` is false.
 * Returns the tip on screen (with its anchor's rect) and the dismiss / turn-off / reset actions.
 *
 * @param {{enabled: boolean}} deps
 */
export function useOnboardingTips({ enabled }) {
  const [shown, setShown] = useState(null); // {id, text, rect, beside, above} | null
  // Bumped by resetTips so the polling effect restarts against the cleared state.
  const [epoch, setEpoch] = useState(0);
  // The persisted {retired, off, visits, ready}, re-read on every poll so another tab's turn-off
  // or retirement shows here. Every change goes through `update` (read, change, write), so this
  // tab never writes back a stale copy over another's.
  const stateRef = useRef(null);
  // This visit's tips shown and stepped aside; mirrored to sessionStorage (saveVisit) so a reload
  // inside the visit keeps its quota and its stepped-aside tips.
  const visitRef = useRef({ snoozed: new Set(), shownIds: new Set() });
  const absentSinceRef = useRef(new Map()); // `after` prerequisites missing from the page, since when
  const currentRef = useRef(null); // {tip, el, since} for the tip on screen
  const dwellRef = useRef(null); // {id, since}: the anchor waiting out DWELL_MS
  const endedAtRef = useRef(-Infinity); // when the last tip went, for NEXT_TIP_GAP_MS
  // Tips shown and not yet given their `ui.tip.end`: one end per showing, whichever path ends it.
  const openRef = useRef(new Set());
  // Set once localStorage fails a read or a write: from then on this page keeps the state in
  // memory, so a turn-off or a dismissal still holds here instead of being re-read away.
  const memoryOnlyRef = useRef(false);

  const saveVisit = useCallback(() => {
    writeVisitState(visitRef.current, defaultSessionStorage());
  }, []);

  const update = useCallback((change) => {
    const storage = memoryOnlyRef.current ? null : defaultLocalStorage();
    const { state, persisted } = updateTipState(storage, change, stateRef.current);
    if (!persisted) memoryOnlyRef.current = true;
    stateRef.current = state;
    return state;
  }, []);

  const hide = useCallback(() => {
    if (currentRef.current) endedAtRef.current = performance.now();
    currentRef.current = null;
    setShown(null);
  }, []);

  const endShowing = useCallback((id, how) => {
    if (openRef.current.delete(id)) recordUiEvent({ event: "ui.tip.end", tip: id, how });
  }, []);

  // Retire `ids` for good. A showing still open ends with `how`; a tip never shown this visit is
  // recorded as `found`.
  const retire = useCallback(
    (ids, how) => {
      // Nothing new to retire: no write (a panel left open names the same tips every poll).
      if (stateRef.current && ids.every((id) => stateRef.current.retired[id])) return;
      let fresh = [];
      update((st) => {
        fresh = ids.filter((id) => !st.retired[id]);
        for (const id of fresh) st.retired[id] = how;
      });
      for (const id of fresh) {
        if (openRef.current.has(id)) endShowing(id, how);
        else if (!visitRef.current.shownIds.has(id))
          recordUiEvent({ event: "ui.tip.end", tip: id, how: "found" });
      }
      if (currentRef.current && fresh.includes(currentRef.current.tip.id)) hide();
    },
    [hide, update, endShowing],
  );

  useEffect(() => {
    if (!enabled) return;
    const session = defaultSessionStorage();
    update((st) => {
      st.visits = countVisit(st, session).visits;
    });
    visitRef.current = readVisitState(session);
  }, [enabled, update]);

  useEffect(() => {
    if (!enabled) return;
    return onIntentBegin((name, params) => {
      const due = tipsReadiedByIntent(TIPS, name, params);
      if (due.length)
        update((st) => {
          for (const id of due) st.ready[id] = true;
        });
      const ids = tipsRetiredByIntent(TIPS, name);
      if (ids.length) retire(ids, "used");
    });
  }, [enabled, retire, update]);

  useEffect(() => {
    if (!enabled) {
      // Turned off under a showing (the phone layout): it ends here, not left open.
      for (const id of [...openRef.current]) endShowing(id, "gone");
      // eslint-disable-next-line react-hooks/set-state-in-effect
      hide();
      return;
    }
    const tick = () => {
      if (!memoryOnlyRef.current) {
        const fresh = tryReadTipState(defaultLocalStorage());
        if (fresh) stateRef.current = fresh;
        else memoryOnlyRef.current = true;
      }
      const s = stateRef.current;
      if (!s) return;
      // Another tab turned tips off, or retired a tip this one still has open: end it here too.
      for (const id of [...openRef.current]) {
        if (s.off) endShowing(id, "turned_off");
        else if (s.retired[id] && !TIP_BY_ID.get(id)?.once) endShowing(id, s.retired[id]);
      }
      const cur = currentRef.current;
      if (s.off) {
        if (cur) hide();
        return;
      }
      const now = performance.now(); // monotonic: dwell and snooze are durations
      // A background tab shows nothing and spends nothing: no dwell, and a shown tip's timeout
      // restarts, so a tip is never shown, retired or timed out with nobody looking.
      if (document.visibilityState === "hidden") {
        dwellRef.current = null;
        if (cur) cur.since = now;
        return;
      }
      const isPresent = surfaceShown;
      const used = [
        ...tipsRetiredBySurfaces(TIPS, isPresent),
        ...tipsRetiredByControls(TIPS, controlShown),
      ].filter((id) => !s.retired[id]);
      if (used.length) {
        retire(used, "used");
        return; // choose on the next poll, from the state that retirement just wrote
      }

      const v = visitRef.current;
      if (cur) {
        // Retired in another tab meanwhile (a `once` tip retires itself when it shows).
        if (s.retired[cur.tip.id] && !cur.tip.once) return hide();
        const inContext =
          (cur.tip.whilePresent ?? []).every(isPresent) &&
          !(cur.tip.whileAbsent ?? []).some(isPresent);
        const el = inContext ? findAnchor(cur.tip) : null;
        if (!el) {
          // This showing is over; a tip still due shows (and is recorded) again if it comes back.
          endShowing(cur.tip.id, "gone");
          return hide();
        }
        if (now - cur.since > SNOOZE_AFTER_MS) {
          v.snoozed.add(cur.tip.id);
          saveVisit();
          endShowing(cur.tip.id, "snoozed");
          return hide();
        }
        cur.el = el;
        return;
      }
      const skippable = trackSkippablePrereqs(TIPS, {
        retired: s.retired,
        shownIds: v.shownIds,
        snoozed: v.snoozed,
        isPresent,
        anchorShown: (id) => !!findAnchor(TIP_BY_ID.get(id)),
        loading: PAGE_LOADING_SURFACES.some(isPresent),
        now,
        absentSince: absentSinceRef.current,
      });
      for (const tip of eligibleTips(TIPS, {
        ...v,
        retired: s.retired,
        visits: s.visits,
        ready: s.ready,
        isPresent,
        skipped: (id) => skippable.has(id),
      })) {
        const el = findAnchor(tip);
        if (!el) continue;
        // With no room beside its dialog a beside tip waits, still due, rather than cover it.
        if (tip.beside && !fitsBeside(rectOf(el), window.innerWidth)) continue;
        const d = dwellRef.current;
        if (!d || d.id !== tip.id) {
          dwellRef.current = { id: tip.id, since: now };
          return;
        }
        // The dwell runs during the gap, so the next tip waits the longer of the two, not both.
        if (now - d.since < DWELL_MS || now - endedAtRef.current < NEXT_TIP_GAP_MS) return;
        dwellRef.current = null;
        currentRef.current = { tip, el, since: now };
        // One shown/end pair per showing; `shownIds` counts tips against the visit's quota.
        recordUiEvent({ event: "ui.tip.shown", tip: tip.id });
        openRef.current.add(tip.id);
        if (!v.shownIds.has(tip.id)) {
          v.shownIds.add(tip.id);
          saveVisit();
        }
        if (tip.once && !s.retired[tip.id])
          // Retired on show, but left on screen until its anchor goes or it is dismissed.
          update((st) => {
            if (!st.retired[tip.id]) st.retired[tip.id] = "shown";
          });
        setShown({
          id: tip.id,
          text: tip.text,
          rect: rectOf(el),
          beside: !!tip.beside,
          above: !!tip.above,
        });
        return;
      }
      dwellRef.current = null;
    };
    const timer = setInterval(tick, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, epoch, retire, hide, saveVisit, update, endShowing]);

  // A click on a control whose action has no intent (plotting a signal) retires the tips that
  // name it in `retireOn.clicks`.
  useEffect(() => {
    if (!enabled) return;
    const onClick = (e) => {
      const uiId = e.target?.closest?.("[data-ui]")?.getAttribute("data-ui");
      const ids = uiId ? tipsRetiredByClick(TIPS, uiId) : [];
      if (ids.length) retire(ids, "used");
    };
    // telemetry-gesture: watches clicks to retire a tip whose feature was used (no operation)
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [enabled, retire]);

  // Browsers throttle a background tab's timers, so its polls may not run at all: restart any
  // dwell and the shown tip's timeout when the tab is shown again.
  useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => {
      dwellRef.current = null;
      if (currentRef.current) currentRef.current.since = performance.now();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [enabled]);

  // Follow the anchor every frame while a tip shows (the timeline pans and zooms under it).
  const shownId = shown?.id ?? null;
  useEffect(() => {
    if (!shownId) return;
    let raf;
    const follow = () => {
      const cur = currentRef.current;
      if (cur?.el?.isConnected) {
        const r = rectOf(cur.el);
        setShown((p) => (p && p.id === cur.tip.id && !sameRect(p.rect, r) ? { ...p, rect: r } : p));
      }
      raf = requestAnimationFrame(follow);
    };
    raf = requestAnimationFrame(follow);
    return () => cancelAnimationFrame(raf);
  }, [shownId]);

  // A press or click anywhere but on the tip means the user is busy with something else: the tip
  // steps aside for the rest of the visit rather than cover what they opened (a menu, a card). Its
  // end is recorded a moment later, so a press that used the feature is recorded once, as `used`.
  useEffect(() => {
    if (!shownId) return;
    const onDown = (e) => {
      if (e.target?.closest?.("[data-tip-layer]")) return;
      const cur = currentRef.current;
      if (!cur) return;
      const id = cur.tip.id;
      visitRef.current.snoozed.add(id);
      saveVisit();
      hide();
      setTimeout(() => endShowing(id, "snoozed"), PRESS_END_MS);
    };
    // telemetry-gesture: a press outside a tip steps it aside (view state, no operation)
    document.addEventListener("pointerdown", onDown, true);
    // A keyboard activation is a click with no press before it; after a handled press, a no-op.
    // telemetry-gesture: a click outside a tip steps it aside (view state, no operation)
    document.addEventListener("click", onDown, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("click", onDown, true);
    };
  }, [shownId, hide, saveVisit, endShowing]);

  const dismissTip = useCallback(() => {
    const cur = currentRef.current;
    if (!cur) return;
    const id = cur.tip.id;
    // A `once` tip retired itself when it showed; retire is a no-op for it.
    if (!cur.tip.once) retire([id], "dismissed");
    endShowing(id, "dismissed");
    hide();
  }, [retire, hide, endShowing]);

  const turnOffTips = useCallback(() => {
    update((st) => {
      st.off = true;
    });
    const cur = currentRef.current;
    if (cur) endShowing(cur.tip.id, "turned_off");
    hide();
  }, [hide, update, endShowing]);

  // Settings → "Show tips again": every tip is due again, and tips are back on. The visit count
  // and what the user has already done (`ready`) are kept.
  const resetTips = useCallback(() => {
    update((st) => {
      st.retired = {};
      st.off = false;
    });
    visitRef.current = { snoozed: new Set(), shownIds: new Set() };
    saveVisit();
    absentSinceRef.current = new Map();
    openRef.current = new Set();
    dwellRef.current = null;
    hide();
    setEpoch((e) => e + 1);
  }, [hide, saveVisit, update]);

  return { tip: shown, dismissTip, turnOffTips, resetTips };
}
